import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Home, LogIn, LogOut, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../connect/api";

const hm = (t) => (t ? t.slice(0, 5) : "");

/** Green Check In / red Check Out card on the Connect home screen — only on approved WFH or training days. Same records as the ERP hub. */
export const WfhCheckIn = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState("");
  const [showLog, setShowLog] = useState(false);
  const load = useCallback(() => api.get("/wfh/status").then((r) => setSt(r.data)).catch(() => setSt({ active: false })), []);
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(t); };
  }, [load]);
  if (!st?.active && !st?.pending_log) return null;

  const mark = async (action, extra = {}) => {
    if ((action === "out" || action === "log") && !log.trim()) { toast.error("Please enter your task log first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/wfh/mark", { action, task_log: log, ...extra });
      setSt(data); setShowLog(false); setLog("");
      toast.success(action === "in" ? `Checked in at ${hm(data.check_in)} — marked Present` : action === "log" ? "Task log saved." : `Checked out at ${hm(data.check_out)}. Task log saved.`);
    } catch (e) { toast.error(errText(e)); if (e?.response?.status === 409) load(); } finally { setBusy(false); }
  };
  const label = st.mode === "training" ? "Training day" : "Approved WFH day";
  const pend = st.pending_log;
  const pendDate = pend ? new Date(`${pend.date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }) : "";

  return (
    <div className="mx-3 mt-3 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3.5 nxc-fade-in" data-testid="wfh-checkin-card">
      {pend && (
        <div className={`rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 ${st.active ? "mb-3" : ""}`} data-testid="wfh-pending-log">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-amber-300"><AlertTriangle size={13} /> Task log needed · {pendDate}</div>
          <p className="text-xs text-slate-300 mt-1.5">You didn't check out, so your day was closed automatically at <b className="text-amber-200">7:00 PM</b> (in {hm(pend.check_in)}). Please add what you worked on.</p>
          <textarea value={log} onChange={(e) => setLog(e.target.value)} rows={3} maxLength={4000} placeholder="What did you work on that day?" className="mt-2 w-full rounded-xl bg-black/30 border border-white/10 focus:border-amber-400/60 text-sm text-slate-100 placeholder:text-slate-600 p-3 outline-none resize-y" data-testid="wfh-pending-log-input" />
          <button onClick={() => mark("log", { date: pend.date })} disabled={busy || !log.trim()} className="mt-2 w-full rounded-xl bg-amber-500 hover:bg-amber-400 text-[#2a1b00] font-semibold py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="wfh-pending-log-save">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Save task log
          </button>
        </div>
      )}
      {st.active && <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-300/90"><Home size={13} /> {label}</div>}
      {st.active && !st.check_in && (
        <>
          <p className="text-xs text-slate-300 mt-1.5">Press Check In when you start work today. Your attendance is marked Present with the time.</p>
          <button onClick={() => mark("in")} disabled={busy} className="mt-3 w-full rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#06281b] font-semibold py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99] transition-transform" data-testid="wfh-checkin-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} Check In
          </button>
        </>
      )}
      {st.active && st.check_in && !st.check_out && (
        <>
          <p className="text-xs text-slate-300 mt-1.5" data-testid="wfh-checkedin-at">Checked in at <b className="text-emerald-200">{hm(st.check_in)}</b> · Present</p>
          {!showLog ? (
            <button onClick={() => setShowLog(true)} className="mt-3 w-full rounded-xl bg-red-500 hover:bg-red-400 text-white font-semibold py-3 text-sm inline-flex items-center justify-center gap-2 active:scale-[0.99] transition-transform" data-testid="wfh-checkout-btn">
              <LogOut size={16} /> Check Out
            </button>
          ) : (
            <div className="mt-3" data-testid="wfh-tasklog-panel">
              <label className="text-[11px] text-slate-400">Today's task log <span className="text-red-300">*</span> — what did you work on?</label>
              <textarea value={log} onChange={(e) => setLog(e.target.value)} rows={4} maxLength={4000} autoFocus placeholder="e.g. Configured 3 client routers, closed 5 support tickets, prepared the weekly report…" className="mt-1 w-full rounded-xl bg-black/30 border border-white/10 focus:border-red-400/60 text-sm text-slate-100 placeholder:text-slate-600 p-3 outline-none resize-y" data-testid="wfh-tasklog-input" />
              <div className="flex gap-2 mt-2">
                <button onClick={() => setShowLog(false)} disabled={busy} className="flex-1 rounded-xl border border-white/15 text-slate-300 py-2.5 text-sm" data-testid="wfh-tasklog-cancel">Cancel</button>
                <button onClick={() => mark("out")} disabled={busy || !log.trim()} className="flex-1 rounded-xl bg-red-500 hover:bg-red-400 text-white font-semibold py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="wfh-checkout-save">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <LogOut size={15} />} Save & Check Out
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {st.active && st.check_in && st.check_out && (
        <div className="mt-1.5 text-xs text-slate-300 flex items-start gap-2" data-testid="wfh-done">
          <CheckCircle2 size={15} className="text-emerald-300 shrink-0 mt-0.5" />
          <span>Present · in <b className="text-emerald-200">{hm(st.check_in)}</b> · out <b className="text-red-200">{hm(st.check_out)}</b>{st.auto_checkout ? " (auto, 7 PM)" : ""}. {st.task_log_pending ? "Add your task log above." : "Task log saved — see you tomorrow."}</span>
        </div>
      )}
    </div>
  );
};
